/**
 * Members API service for managing organization members.
 * @module lib/api/members.api
 */

import {
  PaginationParams,
  PaginatedResponse,
  Member,
  CreateMember,
  MemberSortFieldEnum,
  MemberStatusEnum,
  UpdateMember,
} from '@scrambl/shared';
import { BaseApi } from './base-api';
import { createSingletonApi } from './singleton-factory';

// Extended params type
interface MemberParams extends Partial<PaginationParams> {
  includePermissions?: boolean;
  status?: MemberStatusEnum;
  role?: string;
  search?: string;
  sortBy?: MemberSortFieldEnum;
  [key: string]: string | number | boolean | undefined;
}

interface InviteMembersRequest {
  emails: string[];
  role: string;
  permissionSetId?: string;
}

interface InviteMemberResponse {
  id: string;
  email: string;
  status: string;
  expiresAt: string;
}

export interface MentionSuggestionUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  source?: 'internal' | 'external' | 'portal';
}

export interface MemberRemovalPreview {
  memberId: string;
  memberName: string | null;
  memberEmail: string | null;
  organizationId: string;
  recordCounts: {
    entities: number;
    projects: number;
    tasks: number;
    leads: number;
    deals: number;
    contacts: number;
    threads: number;
    documents: number;
    tagAssignments: number;
    communications: number;
    taskAssignments: number;
    projectMembers: number;
    threadMembers: number;
    noteMembers: number;
    eventInvitees: number;
  };
}

interface MemberRemovalResult {
  success: boolean;
  removedMemberId: string;
  anonymizedUserId: string;
  organizationId: string;
  auditSummary: string;
}

class MemberApiService extends BaseApi<Member, never, UpdateMember> {
  constructor() {
    super({
      baseEndpoint: '/members',
      responseNormalization: true
    });
  }

  // Get member count for subscription seat management
  async getMemberCount(): Promise<{ count: number }> {
    return this.makeRequest<{ count: number }>(`${this.config.baseEndpoint}/count`);
  }

  // Get members for display purposes (avatars, assignees, etc.) - NO PERMISSIONS
  async getMembersForDisplay(params: Omit<MemberParams, 'includePermissions'> = {}): Promise<PaginatedResponse<Member>> {
    const queryString = this.buildQueryParams(params as Record<string, unknown>);
    return this.makeRequest<PaginatedResponse<Member>>(`${this.config.baseEndpoint}/display${queryString}`);
  }

  async getMentionSuggestions(params: {
    moduleType?: string;
    moduleId?: string;
  } = {}): Promise<{ data: MentionSuggestionUser[] }> {
    const queryString = this.buildQueryParams(params as Record<string, unknown>);
    return this.makeRequest<{ data: MentionSuggestionUser[] }>(
      `${this.config.baseEndpoint}/mention-suggestions${queryString}`,
    );
  }

  // Get all members with pagination
  async getMembers(params: MemberParams = {}): Promise<PaginatedResponse<Member>> {
    const listParams = { ...params };

    // Handle includePermissions as a query param
    if (params.includePermissions === true) {
      listParams.includePermissions = true;
    }

    return this.list(listParams);
  }

  // Get a single member by ID
  async getMember(id: string, includePermissions = false): Promise<Member> {
    const queryString = includePermissions ? '?includePermissions=true' : '';
    return this.makeRequest<Member>(`${this.config.baseEndpoint}/${id}${queryString}`);
  }

  async createMember(data: CreateMember): Promise<Member> {
    return this.makeRequest<Member>(`${this.config.baseEndpoint}`, {
      method: 'POST',
      body: data,
    });
  }

  // Invite members to an organization
  async inviteMembers(organizationId: string, data: InviteMembersRequest): Promise<InviteMemberResponse[]> {
    return this.makeRequest<InviteMemberResponse[]>(`/organization-invites/organization/${organizationId}/bulk`, {
      method: 'POST',
      body: {
        invites: data.emails.map(email => ({
          email,
          role: data.role,
          permissionSetId: data.permissionSetId
        }))
      },
    });
  }

  // Update a member
  async updateMember(id: string, data: UpdateMember): Promise<Member> {
    return this.update(id, data);
  }

  // Update the caller's own member profile (identity/contact fields only —
  // the server whitelists; no MEMBER UPDATE permission required)
  async updateMyProfile(data: UpdateMember): Promise<Member> {
    return this.makeRequest<Member>(`${this.config.baseEndpoint}/me/profile`, {
      method: 'PUT',
      body: data,
    });
  }

  // Get removal preview for a member
  async getRemovalPreview(id: string): Promise<MemberRemovalPreview> {
    return this.makeRequest<MemberRemovalPreview>(`${this.config.baseEndpoint}/${id}/removal-preview`);
  }

  // Remove a member from the organization (admin action)
  async removeMember(id: string): Promise<MemberRemovalResult> {
    return this.makeRequest<MemberRemovalResult>(`${this.config.baseEndpoint}/${id}/remove`, {
      method: 'DELETE',
    });
  }

  // Clear cache (no-op - TanStack Query handles caching)
  clearCache(): void {
    // No-op - TanStack Query manages cache
  }
}

// Export singleton instance
export const memberApi = createSingletonApi(MemberApiService);

// Export static-style class for backward compatibility
export class MemberApi {
  static getInstance = () => memberApi;
  static getMembers = (params?: MemberParams) => memberApi.getMembers(params);
  static getMember = (id: string, includePermissions?: boolean) => memberApi.getMember(id, includePermissions);
  static getMembersForDisplay = (params?: Omit<MemberParams, 'includePermissions'>) => memberApi.getMembersForDisplay(params);
  static getMemberCount = () => memberApi.getMemberCount();
  static createMember = (data: CreateMember) => memberApi.createMember(data);
  static updateMember = (id: string, data: UpdateMember) => memberApi.updateMember(id, data);
  static inviteMembers = (organizationId: string, data: InviteMembersRequest) => memberApi.inviteMembers(organizationId, data);
  static getRemovalPreview = (id: string) => memberApi.getRemovalPreview(id);
  static removeMember = (id: string) => memberApi.removeMember(id);
  static clearCache = () => memberApi.clearCache();
}

// Export class for typing
export type { MemberApiService };
export type { MemberParams, InviteMembersRequest, InviteMemberResponse };
